import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Box, Divider, Button, Card, CardContent, Chip, FormControl, Grid, InputLabel, IconButton, CircularProgress, MenuItem, Paper, Select, Stack, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, TablePagination, TextField, Tooltip, Typography } from "@mui/material";
import { RestartAlt, Search, Visibility, ArrowBack } from '@mui/icons-material';
import dayjs from 'dayjs';
import { useNavigate, useSearchParams } from "react-router-dom";
import { API_ENDPOINTS } from "../../../../services/endpoints";
import { getData } from "../../../../services/axiosInstance";
import Loader from "../../../../common/spinner";
import { formatDate } from "../../../../utils/dateFormat";
import { getStatusColor, priorityColors } from '../../../../utils/statusColor.js';

const ChipBox = ({ label, styles }) => (
    <Box sx={{
        minWidth: '80px', // Changed to minWidth
        textAlign: 'center',
        px: 1.5,
        py: 0.5,
        borderRadius: '20px',
        display: 'inline-block',
        fontSize: '0.7rem',
        fontWeight: 700,
        textTransform: 'capitalize',
        whiteSpace: 'nowrap',
        ...styles // This will now correctly apply bgcolor, color, and border
    }}>
        {label}
    </Box>
);

function UserStoriesPage({ epicData, projectData }) {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const userDataRaw = localStorage.getItem('userData');
    const userData = userDataRaw ? JSON.parse(userDataRaw) : null;
    const userRole = userData?.user_type;
    const roleBasePath = userRole === 'Team Lead' ? '/team-lead' : '/manager';
    const projectId = useMemo(() => searchParams.get("projectId"), [searchParams]);
    const projectTitle = useMemo(() => searchParams.get("projectTitle"), [searchParams]);
    const epicId = useMemo(() => searchParams.get("epicId"), [searchParams]);
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState({ stories: [], totalCount: 0, stats: {} });

    const [state, setState] = useState({
        view: 'stories',
        search: '',
        priority: 'All',
        status: 'All',
        timeline: 'All',
        startDate: null,
        endDate: null,
        storyId: null,
        page: 0,
        rowsPerPage: 10
    });
    const [debouncedSearch, setDebouncedSearch] = useState(state.search);

    const updateState = (newState) => setData(prev => ({ ...prev, ...newState }));

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(state.search), 1000);
        return () => clearTimeout(timer);
    }, [state.search]);

    const fetchStoryDetails = useCallback(async () => {
        if (!projectId) return;

        try {
            setLoading(true);

            const filters = {
                search: debouncedSearch || null,
                projectId: projectId,
                epicId: epicId,
                status: state.status === 'All' ? null : state.status,
                priority: state.priority === 'All' ? null : state.priority,
                page: state.page + 1,
                limit: state.rowsPerPage,
            };

            const res = await getData(API_ENDPOINTS.fetchStories, filters);

            if (res.status === "SUCCESS") {
                updateState({
                    stories: res?.message?.stories || [],
                    totalCount: res?.message?.pagination?.totalItems || 0,
                    stats: res?.message?.stats
                });

            } else {
                updateState({ stories: [] });
            }
        } catch (error) {
            updateState({ stories: [] });
        } finally {
            setLoading(false);
        }
    }, [debouncedSearch, state.status, state.priority, state.page, state.rowsPerPage, projectId, epicId]);


    useEffect(() => {
        if (projectId) {
            fetchStoryDetails();
        }
    }, [fetchStoryDetails, projectId]);

    const statsCards = useMemo(() => {
        const s = data?.stats || {};
        return [
            { label: 'Total Stories', value: s.totalStories || 0, bg: '#f5f5f5' },
            { label: 'Ready for Dev', value: s.readyForDevelopment || 0, bg: '#e3f2fd' },
            { label: 'In Development', value: s.inDevelopment || 0, bg: '#f3e5f5' },
            { label: 'Completed', value: s.completed || 0, bg: '#e8f5e9' },
            { label: 'Total Points', value: s.totalStoryPoints || 0, bg: '#fef3c7', color: '#92400e' }
        ];
    }, [data]);

    const filteredStories = useMemo(() => {
        const list = data?.stories || [];
        return list.filter(s => {
            const titleStr = [s.user_story_title, s.title, s.story_code]
                .filter(Boolean)
                .join(' ')
                .toLowerCase();
            const matchesSearch = !state.search || titleStr.includes(state.search.toLowerCase());

            const matchesPriority = state.priority === 'All' ||
                String(s.priority).toLowerCase() === state.priority.toLowerCase();

            const matchesStatus = state.status === 'All' ||
                String(s.status).toLowerCase() === state.status.toLowerCase();

            let matchesTimeline = true;
            if (state.startDate && state.endDate) {
                const storyDate = dayjs(s.due_date || s.created_at);
                if (storyDate.isValid()) {
                    matchesTimeline = storyDate.isBetween(state.startDate, state.endDate, null, '[]');
                } else {
                    matchesTimeline = false;
                }
            }

            return matchesSearch && matchesPriority && matchesStatus && matchesTimeline;
        });
    }, [data.stories, state.search, state.priority, state.status, state.startDate, state.endDate]);

    const paginatedStories = useMemo(() => {
        const startIndex = state.page * state.rowsPerPage;
        return filteredStories.slice(startIndex, startIndex + state.rowsPerPage);
    }, [filteredStories, state.page, state.rowsPerPage]);

    const resetFilters = () => {
        setState(p => ({
            ...p,
            search: '',
            priority: 'All',
            status: 'All',
            timeline: 'All',
            startDate: null,
            endDate: null,
            page: 0
        }));
    };

    if (loading) {
        return <Loader />;
    }

    const StoryMobileCard = ({ story }) => (
        <Card sx={{ borderRadius: '14px', boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}>
            <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600, color: '#3b82f6' }}>
                            {story.story_code}
                        </Typography>
                        <Typography sx={{ fontWeight: 700, color: '#1e293b', wordBreak: 'break-word' }}>
                            {story.user_story_title || story.title}
                        </Typography>
                    </Box>
                    <Tooltip title="View Story">
                        <IconButton
                            size="small"
                            sx={{ flexShrink: 0 }}
                            onClick={() => navigate(`${roleBasePath}/user-story-details?storyId=${story.id}`)}
                        >
                            <Visibility fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Stack>

                <Stack direction="row" spacing={1} alignItems="center" justifyContent={'space-between'} sx={{ mt: 1.5, flexWrap: 'wrap', gap: 1 }}>
                    <Stack direction={'row'} sx={{flexWrap: 'wrap', gap: 1}}>
                        <ChipBox
                            label={story.status}
                            styles={{
                                bgcolor: getStatusColor(story.status).bg,
                                color: getStatusColor(story.status).color,
                                border: `1px solid ${getStatusColor(story.status).border}`
                            }}
                        />
                        <ChipBox
                            label={story.priority}
                            styles={{
                                bgcolor: priorityColors[story.priority]?.bg || '#f1f5f9',
                                color: priorityColors[story.priority]?.color || '#64748b',
                                border: `1px solid ${priorityColors[story.priority]?.border || '#cbd5e1'}`
                            }}
                        />
                    </Stack>
                    <Box>
                        <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.secondary' }}>
                            {`${Math.round(story.progress || 0)}% · ${story.story_points || 0} pts`}
                        </Typography>
                    </Box>
                </Stack>

                <Divider sx={{ my: 1.5 }} />

                {(!story.start_date && !story.due_date) ? (
                    <Typography variant="caption" sx={{ color: '#ef4444', fontWeight: '600' }}>
                        Timeline not available
                    </Typography>
                ) : !story.due_date ? (
                    <Stack direction="row" justifyContent="space-between">
                        <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>{formatDate(story.start_date)}</Typography>
                        <Typography variant="caption" sx={{ color: '#f59e0b', fontWeight: '500', fontSize: '0.7rem' }}>End date pending</Typography>
                    </Stack>
                ) : (
                    <Stack direction="row" justifyContent="space-between">
                        <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>{formatDate(story.start_date)}</Typography>
                        <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.7rem' }}>to {formatDate(story.due_date)}</Typography>
                    </Stack>
                )}
            </CardContent>
        </Card>
    );

    return (
        <Box>
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                alignItems={{ xs: 'flex-start', sm: 'center' }}
                sx={{ borderBottom: '1px solid #c3c3c3', px: { xs: 1, sm: 2 }, py: { xs: 1, sm: 0 } }}
            >
                <IconButton
                    onClick={() => navigate(-1)}
                    sx={{ color: '#64748b', borderRadius: '0px', p: { xs: 0, sm: 1 }, '&:hover': { bgcolor: '#e0e3e6' } }}
                >
                    <ArrowBack sx={{ fontSize: 20 }} />
                    <Typography variant="body2" sx={{ ml: 1, fontWeight: 500, color: '#475569' }}>Back</Typography>
                </IconButton>

                <Divider orientation="vertical" flexItem sx={{ mx: 2, height: 24, alignSelf: 'center', background: '#e2e8f0', display: { xs: 'none', sm: 'block' } }} />

                {/* flexGrow: 1 tells this Box to take up all remaining horizontal space */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexGrow: 1, py: { xs: 1, sm: 1 }, flexWrap: 'wrap', gap: 1, width: { xs: '100%', sm: 'auto' } }}>
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, gap: { xs: 1, sm: 2 }, flexWrap: 'wrap' }}>
                        <Typography variant="h5" fontWeight="bold">User Stories</Typography>
                        <Tooltip title="Project Name" arrow>
                            <Chip label={projectTitle || "N/A"} size="small" variant="outlined" sx={{ cursor: 'pointer' }} />
                        </Tooltip>
                    </Box>
                </Box>
            </Stack>

            {/* Stats Grid */}
            <Grid container spacing={1.5} sx={{ my: 3 }}>
                {statsCards.map((s, i) => (
                    <Grid size={{ xs: 6, sm: 4, md: 2.4 }} key={i}>
                        <Card sx={{ p: 1.5, textAlign: 'center', bgcolor: s.bg, borderRadius: 2, height: '100%', border: '1px solid rgba(0,0,0,0.05)' }} elevation={0}>
                            <Typography variant="h6" sx={{ color: s.color || 'inherit', fontWeight: 800 }}>{s.value}</Typography>
                            <Typography variant="caption" color="textSecondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>{s.label}</Typography>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* Filters */}
            <Paper elevation={0} sx={{ p: 2, mb: 2, border: '1px solid #e2e8f0', borderRadius: 2, bgcolor: '#f8fafc' }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid size={{ xs: 12, sm: 5 }}>
                        <TextField fullWidth size="small" placeholder="Search by title..." value={state.search} onChange={(e) => setState(p => ({ ...p, search: e.target.value, page: 0 }))} InputProps={{ startAdornment: <Search fontSize="small" sx={{ mr: 1, color: 'gray' }} />, sx: { bgcolor: '#fff' } }} />
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Priority</InputLabel>
                            <Select label="Priority" value={state.priority} onChange={(e) => setState(p => ({ ...p, priority: e.target.value, page: 0 }))} sx={{ bgcolor: '#fff' }} >
                                <MenuItem value="All">All</MenuItem>
                                <MenuItem value="High">High</MenuItem>
                                <MenuItem value="Medium">Medium</MenuItem>
                                <MenuItem value="Low">Low</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid size={{ xs: 6, sm: 3 }}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Status</InputLabel>
                            <Select label="Status" value={state.status} onChange={(e) => setState(p => ({ ...p, status: e.target.value, page: 0 }))} sx={{ bgcolor: '#fff' }} >
                                <MenuItem value="All">All</MenuItem>
                                <MenuItem value="Ready for Dev">Ready for Dev</MenuItem>
                                <MenuItem value="In Development">In Development</MenuItem>
                                <MenuItem value="Completed">Completed</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 1 }}>
                        <Button fullWidth onClick={resetFilters} sx={{ textTransform: 'none', color: 'text.secondary', minWidth: 0, px: { sm: 0, md: 1 } }} >
                            <RestartAlt fontSize="small" />
                            <Box component="span" sx={{ display: { xs: 'inline', sm: 'none', lg: 'inline' }, ml: 0.5 }}>Reset</Box>
                        </Button>
                    </Grid>
                </Grid>
            </Paper>

            {/* Table */}
            {filteredStories.length > 0 ? (
                <>
                    <Stack spacing={1.5} sx={{ display: { xs: 'flex', md: 'none' } }}>
                        {paginatedStories.map((story) => (
                            <StoryMobileCard key={story.id || story.story_id} story={story} />
                        ))}
                    </Stack>

                    <TableContainer component={Paper} sx={{ display: { xs: 'none', md: 'block' }, borderRadius: '12px', boxShadow: '0 10px 30px rgba(0,0,0,0.04)', overflow: 'hidden', border: '1px solid #E2E8F0', bgcolor: '#FFFFFF' }}>
                        <Box sx={{ overflowX: 'auto' }}>
                            <Table sx={{ minWidth: 900 }}>
                                <TableHead sx={{ bgcolor: '#EFF6FF' }}>
                                    <TableRow>
                                        <TableCell sx={{ color: '#1D4ED8', fontWeight: 700, borderBottom: '1px solid #E5E7EB', width: '350px' }}>Story Name</TableCell>
                                        <TableCell sx={{ color: '#1D4ED8', fontWeight: 700, borderBottom: '1px solid #E5E7EB' }}>Points</TableCell>
                                        <TableCell sx={{ color: '#1D4ED8', fontWeight: 700, borderBottom: '1px solid #E5E7EB' }}>Progress</TableCell>
                                        <TableCell sx={{ color: '#1D4ED8', fontWeight: 700, borderBottom: '1px solid #E5E7EB' }}>Timeline</TableCell>
                                        <TableCell sx={{ color: '#1D4ED8', fontWeight: 700, borderBottom: '1px solid #E5E7EB' }}>Priority</TableCell>
                                        <TableCell sx={{ color: '#1D4ED8', fontWeight: 700, borderBottom: '1px solid #E5E7EB' }}>Status</TableCell>
                                        <TableCell align="center" sx={{ color: '#1D4ED8', fontWeight: 700, borderBottom: '1px solid #E5E7EB' }}>Action</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody sx={{ bgcolor: '#FFFFFF' }}>
                                    {paginatedStories.map((story) => (
                                        <TableRow key={story.id || story.story_id} hover>
                                            <TableCell sx={{ color: '#1E293B', maxWidth: 400 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#3b82f6', whiteSpace: 'nowrap' }} >
                                                        {story.story_code}
                                                    </Typography>

                                                    <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b', overflow: 'hidden', textOverflow: 'ellipsis', display: '-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient: 'vertical' }}>
                                                        {story.user_story_title || story.title}
                                                    </Typography>
                                                </Box>
                                            </TableCell>

                                            <TableCell sx={{ color: '#1E293B', fontWeight: 700 }}>{story.story_points || 0}</TableCell>
                                            <TableCell sx={{ color: '#1E293B' }}>
                                                <Box sx={{ position: 'relative', display: 'inline-flex', verticalAlign: 'middle' }}>
                                                    {/* Background Circle */}
                                                    <CircularProgress
                                                        variant="determinate"
                                                        value={100}
                                                        size={42}
                                                        thickness={4}
                                                        sx={{ color: '#e2e8f0' }}
                                                    />
                                                    {/* Active Progress Circle */}
                                                    <CircularProgress
                                                        variant="determinate"
                                                        value={Math.min(story.progress || 0, 100)}
                                                        size={42}
                                                        thickness={4}
                                                        sx={{
                                                            color: story.progress >= 100 ? '#22c55e' : '#6366f1',
                                                            position: 'absolute',
                                                            left: 0,
                                                            [`& .MuiCircularProgress-circle`]: { strokeLinecap: 'round' },
                                                        }}
                                                    />
                                                    {/* Percentage Text Overlay */}
                                                    <Box
                                                        sx={{
                                                            top: 0, left: 0, bottom: 0, right: 0,
                                                            position: 'absolute',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                        }}
                                                    >
                                                        <Typography variant="caption" sx={{ fontSize: '0.65rem', fontWeight: 800, color: 'text.secondary' }}>
                                                            {`${Math.round(story.progress || 0)}%`}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ color: '#1E293B' }}>
                                                {(!story.start_date && !story.due_date) ? (
                                                    <Typography variant="caption" sx={{ color: '#ef4444', fontWeight: '600' }}>
                                                        Timeline not available
                                                    </Typography>
                                                ) : !story.due_date ? (
                                                    <Stack>
                                                        <Typography variant="caption" display="block" fontWeight="500">
                                                            {formatDate(story.start_date)}
                                                        </Typography>
                                                        <Typography variant="caption" sx={{ color: '#f59e0b', fontWeight: '500' }}>
                                                            End date pending
                                                        </Typography>
                                                    </Stack>
                                                ) : (
                                                    <>
                                                        <Typography variant="caption" display="block" fontWeight="500">
                                                            {formatDate(story.start_date)}
                                                        </Typography>
                                                        <Typography variant="caption" color="textSecondary">
                                                            to {formatDate(story.due_date)}
                                                        </Typography>
                                                    </>
                                                )}
                                            </TableCell>
                                            <TableCell sx={{ color: '#1E293B' }}>
                                                <ChipBox
                                                    label={story.priority}
                                                    styles={{
                                                        bgcolor: priorityColors[story.priority]?.bg || '#f1f5f9',
                                                        color: priorityColors[story.priority]?.color || '#64748b',
                                                        border: `1px solid ${priorityColors[story.priority]?.border || '#cbd5e1'}`,
                                                        width: '80px'
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell sx={{ color: '#1E293B' }}>
                                                <ChipBox
                                                    label={story.status}
                                                    styles={{
                                                        bgcolor: getStatusColor(story.status).bg,
                                                        color: getStatusColor(story.status).color,
                                                        border: `1px solid ${getStatusColor(story.status).border}`,
                                                        width: 'fit-content'
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell display='flex' align="center" sx={{ color: '#1E293B' }}>
                                                <Tooltip title="View Story">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => navigate(`${roleBasePath}/user-story-details?storyId=${story.id}`)}
                                                    >
                                                        <Visibility fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={filteredStories.length}
                        page={state.page}
                        onPageChange={(e, newPage) => setState(p => ({ ...p, page: newPage }))}
                        rowsPerPage={state.rowsPerPage}
                        onRowsPerPageChange={(e) => setState(p => ({ ...p, rowsPerPage: parseInt(e.target.value, 10), page: 0 }))}
                        rowsPerPageOptions={[5, 10, 25]}
                    />
                </>
            ) : (
                <Box sx={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center',
                    justifyContent: 'center', py: 8, bgcolor: '#fff', borderRadius: 2,
                    border: '1px dashed #cbd5e1'
                }}>
                    <Typography variant="h6" fontWeight="bold">No Stories Found</Typography>
                    <Typography variant="body2" color="textSecondary">Try adjusting your filters or search keywords.</Typography>
                </Box>
            )}
        </Box>
    );
}

export default UserStoriesPage;